import React, { useEffect } from 'react';
import proptypes from 'prop-types';
import { Skeleton, SkeletonLine } from '@one-thd/sui-atomic-components';
import { dataModel } from './dataModel';
import { useApplianceFuelType } from './hooks/useApplianceFuelType';
import { FuelTypeProvider, FuelTypeDrawer, FuelTypeAlert } from './components';
import { ENTRY_TYPES } from './constants';

const { string, bool, shape, arrayOf, oneOf } = proptypes;

export const ApplianceFuelType = ({
  itemId,
  entryType,
  position,
  drawerStatus: drawerStatusProp,
  product,
  metadata,
  search,
  allowHeader,
  imageCacheEnabled
}) => {
  useEffect(() => { LIFE_CYCLE_EVENT_BUS.lifeCycle.trigger('appliance-fuel-type.ready'); }, []);

  const {
    drawerStatus,
    shouldShowHeader,
    headerName,
    headerLabel,
    isLoading,
    error,
    fuelTypeWillRender
  } = useApplianceFuelType({
    itemId,
    drawerStatus: drawerStatusProp,
    product,
    metadata,
    search,
    entryType,
    allowHeader,
    imageCacheEnabled
  });

  if (isLoading) {
    return (
      <Skeleton
        data-testid="appliance-fuel-type-loader"
        grow
        disablePadding
      >
        <SkeletonLine variant="single" />
      </Skeleton>
    );
  } else if (!fuelTypeWillRender) {
    return null;
  }

  return (
    <div
      data-testid="appliance-fuel-type"
      data-component="ApplianceFuelType"
    >
      <FuelTypeProvider
        entryType={entryType}
        position={position}
        header={{
          shouldShowHeader,
          headerName,
          headerLabel
        }}
        drawerStatus={drawerStatus}
      >
        <FuelTypeAlert />
        <FuelTypeDrawer />
      </FuelTypeProvider>
    </div>
  );
};

ApplianceFuelType.propTypes = {
  itemId: string,
  entryType: oneOf(ENTRY_TYPES),
  position: oneOf(['left', 'right']),
  allowHeader: bool,
  imageCacheEnabled: bool,
  drawerStatus: shape({
    types: arrayOf(string).isRequired,
    categories: arrayOf(string).isRequired,
    isDryer: bool,
    isRange: bool,
    hasElectric: bool,
    hasGas: bool,
    hasInduction: bool,
    hasDualFuel: bool,
    hasHeatpump: bool
  }),
  product: shape({
    itemId: string,
    dataSource: string,
    identifiers: shape({
      isSuperSku: bool,
      parentId: string
    }),
    specificationGroup: arrayOf(shape({
      specTitle: string,
      specifications: arrayOf(shape({
        specName: string,
        specValue: string
      }))
    }))
  }),
  metadata: shape({
    attributes: arrayOf(shape({
      attributeValues: arrayOf(shape({
        value: string
      })),
      attributeName: string
    })),
    superDuperSku: shape({
      attributes: arrayOf(shape({
        attributeName: string,
        attributeValues: arrayOf(shape({
          selected: bool,
          value: string
        }))
      }))
    })
  }),
  search: shape({
    dimensions: arrayOf(shape({
      label: string,
      refinements: arrayOf(shape({
        label: string
      }))
    })),
    primaryFilters: arrayOf(shape({
      label: string,
      refinements: arrayOf(shape({
        label: string
      }))
    }))
  })
};

ApplianceFuelType.defaultProps = {
  itemId: '',
  entryType: '',
  position: 'right',
  imageCacheEnabled: false,
  allowHeader: false,
  drawerStatus: null,
  product: null,
  metadata: null,
  search: null
};

ApplianceFuelType.dataModel = dataModel;

ApplianceFuelType.displayName = 'ApplianceFuelType';
